import type { TFunction } from 'i18next';
import type { TransportRouteSummaryDto } from '../types/transport';

export interface WZTransportSnapshot {
  transportCompany?: string;
  transportInvoiceNo?: string;
  transportPrice?: number;
  vehicleRegistration?: string;
  driverName?: string;
  deliveryAddress?: string;
}

export interface WZTransportFormPatchResult {
  patch: WZTransportSnapshot;
  snapshot: WZTransportSnapshot;
  selectedTransports: TransportRouteSummaryDto[];
}

function uniqueJoined(values: Array<string | undefined | null>) {
  return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean))).join(', ');
}

function getTransportAmount(transport: TransportRouteSummaryDto) {
  return transport.transportRate ?? transport.transportCost;
}

function getTransportVehicleRegistration(transport: TransportRouteSummaryDto) {
  return transport.vehicleRegistration || transport.licensePlate1 || transport.licensePlate2 || '';
}

export function buildWZTransportOption(transport: TransportRouteSummaryDto, t: TFunction) {
  const routeLabel = transport.routeNumber || transport.id;
  const companyLabel = transport.transportCompany || '-';
  const orderLabel = transport.orderNumber ? ` · ${transport.orderNumber}` : '';
  const statusLabel = transport.status
    ? ` · ${t(`transport:status.${transport.status.toLowerCase()}`, { defaultValue: t('common:unknownStatus') })}`
    : '';

  return {
    value: transport.id,
    label: `${routeLabel} · ${companyLabel}${orderLabel}${statusLabel}`,
  };
}

export function buildWZTransportSnapshot(
  selectedTransports: TransportRouteSummaryDto[],
  canEditPurchasePrices: boolean,
): WZTransportSnapshot {
  const transportAmounts = selectedTransports
    .map(getTransportAmount)
    .filter((value): value is number => value !== null && value !== undefined);

  return {
    transportCompany: uniqueJoined(selectedTransports.map((transport) => transport.transportCompany)),
    transportInvoiceNo: uniqueJoined(selectedTransports.map((transport) => transport.invoiceNumber)),
    transportPrice: canEditPurchasePrices && transportAmounts.length > 0
      ? transportAmounts.reduce((sum, value) => sum + Number(value), 0)
      : undefined,
    vehicleRegistration: uniqueJoined(selectedTransports.map(getTransportVehicleRegistration)),
    driverName: uniqueJoined(selectedTransports.map((transport) => transport.driverName)),
    deliveryAddress: uniqueJoined(selectedTransports.flatMap((transport) => transport.deliveryAddresses || [])),
  };
}

export function normalizeWZTransportIds(transportIds?: string[] | string | null): string[] {
  const rawIds = Array.isArray(transportIds)
    ? transportIds
    : transportIds
      ? [transportIds]
      : [];

  return Array.from(new Set(rawIds.map((transportId) => String(transportId).trim()).filter(Boolean)));
}

export function mergeWZTransportOptions(
  orderTransports: TransportRouteSummaryDto[],
  linkedTransports: TransportRouteSummaryDto[] = [],
): TransportRouteSummaryDto[] {
  const byId = new Map<string, TransportRouteSummaryDto>();

  [...orderTransports, ...linkedTransports].forEach((transport) => {
    if (transport.id) {
      byId.set(transport.id, transport);
    }
  });

  return Array.from(byId.values());
}

export function buildWZTransportFormPatch(
  transports: TransportRouteSummaryDto[],
  transportIds: string[] | string | null | undefined,
  canEditPurchasePrices: boolean,
  currentDeliveryAddress?: string,
): WZTransportFormPatchResult {
  const selectedIds = normalizeWZTransportIds(transportIds);

  if (selectedIds.length === 0) {
    const emptyPatch = {
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    };

    return {
      patch: emptyPatch,
      snapshot: {},
      selectedTransports: [],
    };
  }

  const selectedTransports = selectedIds
    .map((transportId) => transports.find((item) => item.id === transportId))
    .filter((transport): transport is TransportRouteSummaryDto => Boolean(transport));

  if (selectedTransports.length === 0) {
    return {
      patch: {},
      snapshot: {},
      selectedTransports,
    };
  }

  const snapshot = buildWZTransportSnapshot(selectedTransports, canEditPurchasePrices);

  return {
    patch: {
      transportCompany: snapshot.transportCompany,
      transportInvoiceNo: snapshot.transportInvoiceNo,
      transportPrice: snapshot.transportPrice,
      vehicleRegistration: snapshot.vehicleRegistration,
      driverName: snapshot.driverName,
      deliveryAddress: snapshot.deliveryAddress || currentDeliveryAddress,
    },
    snapshot,
    selectedTransports,
  };
}
